import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, Key } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AgencyUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordData, setPasswordData] = useState({ newPassword: "", confirmPassword: "" });
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserEmail, setCurrentUserEmail] = useState("");

  useEffect(() => {
    fetchUsers();
    // Get current user info from localStorage
    const token = localStorage.getItem('token');
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setCurrentUserEmail(payload.sub);
      setCurrentUserId(payload.user_id);
    }
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/agency-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to fetch agency users");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = () => {
    setShowPasswordDialog(true);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    // Check password requirements
    const hasDigit = /\d/.test(passwordData.newPassword);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(passwordData.newPassword);
    
    if (!hasDigit || !hasSpecial) {
      toast.error("Password must contain at least 1 digit and 1 special character");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/users/${currentUserId}/change-password`,
        { new_password: passwordData.newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Password changed successfully");
      setShowPasswordDialog(false);
      setPasswordData({ newPassword: "", confirmPassword: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-8" style={{ background: '#0A1128', minHeight: '100vh' }}>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold" style={{ color: '#FFFFFF' }}>Agency Users</h1>
      </div>

      <Card style={{ background: '#1E293B', borderColor: '#334155' }}>
        <CardHeader>
          <CardTitle style={{ color: '#FFFFFF' }}>Users in Your Agency</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: '1px solid #334155' }}>
                  <th className="text-left p-3" style={{ color: '#94A3B8' }}>Full Name</th>
                  <th className="text-left p-3" style={{ color: '#94A3B8' }}>Email</th>
                  <th className="text-left p-3" style={{ color: '#94A3B8' }}>Role</th>
                  <th className="text-left p-3" style={{ color: '#94A3B8' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center p-6" style={{ color: '#94A3B8' }}>
                      No users found
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id} style={{ borderBottom: '1px solid #334155' }}>
                      <td className="p-3" style={{ color: '#FFFFFF' }}>
                        {user.full_name || 'N/A'}
                        {user.email === currentUserEmail && (
                          <span className="ml-2 text-xs px-2 py-1 rounded" style={{ background: '#FFD700', color: '#0A1128' }}>
                            You
                          </span>
                        )}
                      </td>
                      <td className="p-3" style={{ color: '#FFFFFF' }}>{user.email}</td>
                      <td className="p-3">
                        <span className="px-2 py-1 rounded text-xs" style={{ 
                          background: user.role === 'admin' ? '#3B82F6' : '#10B981',
                          color: '#FFFFFF'
                        }}>
                          {user.role || 'N/A'}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className="px-2 py-1 rounded text-xs" style={{ 
                          background: user.status === 'approved' ? '#10B981' : 
                                      user.status === 'pending' ? '#F59E0B' : '#EF4444',
                          color: '#FFFFFF'
                        }}>
                          {user.status || 'N/A'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

    </div>
  );
};

export default AgencyUsers;
